import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import PackageDownloadModal from "./PackageDownloadModal";
import { getCurrentSiteId, setCurrentSiteId } from "@/utils/api";

const menuGroups = [
  {
    label: "核心管理",
    items: [
      { icon: "ri-dashboard-line", label: "数据概览", path: "/admin" },
      { icon: "ri-file-list-3-line", label: "信息管理", path: "/admin/listings" },
      { icon: "ri-add-circle-line", label: "发布信息", path: "/admin/publish" },
      { icon: "ri-user-3-line", label: "用户管理", path: "/admin/users" },
      { icon: "ri-alarm-warning-line", label: "举报管理", path: "/admin/reports" },
      { icon: "ri-vip-crown-line", label: "VIP订单", path: "/admin/vip-orders" },
      { icon: "ri-pushpin-line", label: "置顶管理", path: "/admin/pin-management" },
    ],
  },
  {
    label: "内容配置",
    items: [
      { icon: "ri-layout-grid-line", label: "分类设置", path: "/admin/categories" },
      { icon: "ri-layout-top-line", label: "栏目管理", path: "/admin/nav-menus" },
      { icon: "ri-pages-line", label: "单页管理", path: "/admin/single-pages" },
      { icon: "ri-cloud-line", label: "云端数据", path: "/admin/cloud-data" },
    ],
  },
  {
    label: "系统设置",
    items: [
      { icon: "ri-bank-card-line", label: "支付配置", path: "/admin/payment" },
      { icon: "ri-settings-3-line", label: "网站设置", path: "/admin/settings" },
      { icon: "ri-paint-brush-line", label: "网站装修", path: "/admin/decoration" },
    ],
  },
];

// Flatten for breadcrumb lookup
const allMenuItems = menuGroups.flatMap((g) => g.items);

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [isSiteAdmin, setIsSiteAdmin] = useState(false);
  const [currentSiteId, setCurrentSiteIdState] = useState<string>("");

  // 判断是否为站点管理员
  useEffect(() => {
    try {
      const userInfo = localStorage.getItem('user_info');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        // site_admin 是站点管理员，不能使用后台管理功能
        setIsSiteAdmin(user.role === 'site_admin');
        console.log('AdminLayout - 用户角色:', user.role, '- 是否站点管理员:', user.role === 'site_admin');
      }
      setCurrentSiteIdState(getCurrentSiteId() || "");
    } catch (e) {
      console.error('解析用户信息失败:', e);
    }
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // 切换到指定站点
  const handleSwitchSite = (siteId: string) => {
    setCurrentSiteId(siteId);
    setCurrentSiteIdState(siteId);
    // 站点管理员切换站点后跳转到该站点的后台
    if (isSiteAdmin) {
      navigate("/admin");
    }
  };

  const currentLabel =
    allMenuItems.find((m) =>
      m.path === "/admin"
        ? location.pathname === "/admin"
        : location.pathname.startsWith(m.path)
    )?.label || "后台管理";

  // 站点管理员只能访问 /admin/my-sites
  if (isSiteAdmin && location.pathname !== "/admin/my-sites") {
    // 重定向到我的站点页面
    if (!location.pathname.startsWith("/admin")) {
      // 已经不在 /admin 路径下，无需处理
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar - 站点管理员不显示 */}
      {!isSiteAdmin && (
        <aside
          className={`fixed left-0 top-0 h-full bg-gray-900 text-white transition-all duration-300 z-40 flex flex-col ${
            sidebarCollapsed ? "w-16" : "w-56"
          }`}
        >
          {/* Logo */}
          <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-700/50 shrink-0">
            <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-lg shrink-0">
              <i className="ri-shield-star-line text-white text-base" />
            </div>
            {!sidebarCollapsed && (
              <div>
                <div className="text-sm font-bold text-white">后台管理</div>
                <div className="text-xs text-gray-400">Admin Panel</div>
              </div>
            )}
          </div>

          {/* Nav */}
          <nav className="flex-1 py-3 overflow-y-auto">
            {menuGroups.map((group) => (
              <div key={group.label} className="mb-1">
                {!sidebarCollapsed && (
                  <div className="px-4 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {group.label}
                  </div>
                )}
                {sidebarCollapsed && (
                  <div className="mx-2 my-1 border-t border-gray-700/40" />
                )}
                {group.items.map((item) => {
                  const isActive =
                    item.path === "/admin"
                      ? location.pathname === "/admin"
                      : location.pathname.startsWith(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      title={sidebarCollapsed ? item.label : undefined}
                      className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg mb-0.5 transition-colors ${
                        isActive
                          ? "bg-orange-500 text-white"
                          : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-base`} />
                      </div>
                      {!sidebarCollapsed && (
                        <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Bottom */}
          <div className="border-t border-gray-700/50 p-3 shrink-0">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="flex items-center gap-3 px-2 py-2 w-full rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className={`${sidebarCollapsed ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} text-base`} />
              </div>
              {!sidebarCollapsed && <span className="text-sm">收起菜单</span>}
            </button>
          </div>
        </aside>
      )}

      {/* Main */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${!isSiteAdmin && !sidebarCollapsed ? "ml-56" : !isSiteAdmin ? "ml-16" : "ml-0"}`}>
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 h-14 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-orange-500 transition-colors flex items-center gap-1">
              <i className="ri-home-4-line" />
              <span>前台首页</span>
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-800 font-medium">{currentLabel}</span>
          </div>

          <div className="flex items-center gap-3">
            {/* 站点管理员显示「我的站点」入口 */}
            {isSiteAdmin ? (
              <Link
                to="/admin/my-sites"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap font-medium"
                title="我的站点"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-global-line text-sm" />
                </div>
                <span>我的站点</span>
              </Link>
            ) : (
              <>
                <Link
                  to="/admin/my-sites"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  title="站点管理"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-global-line text-sm" />
                  </div>
                  <span>站点管理</span>
                </Link>
                <Link
                  to="/tenant"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  title="租户管理"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-building-line text-sm" />
                  </div>
                  <span>租户管理</span>
                </Link>
                <button
                  onClick={() => setShowPackageModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  title="打包下载"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-download-cloud-line text-sm" />
                  </div>
                  <span>打包下载</span>
                </button>
              </>
            )}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-full">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shield-user-line text-orange-500 text-sm" />
              </div>
              <span className="text-sm font-medium text-orange-600">{user?.nickname}</span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-logout-box-r-line text-sm" />
              </div>
              <span>退出</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>

      {showPackageModal && (
        <PackageDownloadModal onClose={() => setShowPackageModal(false)} />
      )}
    </div>
  );
}
